// src/components/common/UploadGuidelinesModal.tsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface UploadGuidelinesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GuidelineItem {
  title: string;
  description: string;
}

export const UploadGuidelinesModal: React.FC<UploadGuidelinesModalProps> = ({
  isOpen,
  onClose
}) => {
  const { t } = useTranslation('create');

  // Get guidelines items from translation
  const guidelinesItems = t('guidelines.modal.items', { returnObjects: true }) as GuidelineItem[];

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white w-full max-w-md mx-4 rounded-2xl shadow-xl overflow-hidden max-h-[80vh] flex flex-col"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {t('guidelines.modal.title')}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-4">
                {Array.isArray(guidelinesItems) && guidelinesItems.map((guideline, index) => (
                  <div
                    key={index}
                    className="p-4 bg-gray-50 rounded-xl"
                  >
                    <h3 className="font-semibold text-gray-900 mb-1.5 text-sm">{guideline.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{guideline.description}</p>
                  </div>
                ))}
              </div>

              {/* 確認メッセージ */}
              <div className="mt-6 p-4 bg-purple-50 rounded-xl">
                <p className="text-sm text-purple-800">
                  {t('guidelines.modal.agreement')}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100">
              <button
                onClick={onClose}
                className="w-full py-2.5 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors"
              >
                {t('guidelines.modal.close')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default UploadGuidelinesModal;
